// HistorieBarChart.tsx — hand-rolled responsive SVG bar chart, shared by the three
// per-year bar views on the Historie page (Kalenderjahr-Renditen, Ausschüttungen/Jahr,
// Ausschüttungsgleiche Erträge/Jahr). Same viewBox + rotated-category-label technique as
// WaterfallChart.tsx (no chart library, reads at 360px mobile up through desktop) —
// simplified for a flat list of same-kind bars instead of a running-total waterfall.
import { readCssVar } from '../lib/theme.js';

export interface HistorieBar {
  key: string;
  label: string;
  value: number;
}

/** 'signed': each bar colored by its own sign (--positive/--negative — Kalenderjahr-
 * Renditen). 'accent': every bar the same current --accent (Ausschüttungen/AGE — always
 * non-negative "amount" bars, not gain/loss). */
export type HistorieBarMode = 'signed' | 'accent';

export interface HistorieBarChartProps {
  bars: HistorieBar[];
  mode: HistorieBarMode;
  valueFmt: (v: number) => string;
  emptyMessage?: string;
}

const VB_WIDTH = 360;
const VB_HEIGHT = 190;
const PLOT_TOP = 22;
const PLOT_BOTTOM = 122;
const GAP = 5;
const LABEL_PAD_LEFT = 14;

const COLOR_POSITIVE = '#4ade80';
const COLOR_NEGATIVE = '#f87171';

export function HistorieBarChart({ bars, mode, valueFmt, emptyMessage }: HistorieBarChartProps) {
  const accent = readCssVar('--accent', '#60a5fa');

  if (bars.length === 0) {
    return <div class="empty-state">{emptyMessage ?? 'Keine Daten vorhanden.'}</div>;
  }

  const values = bars.map((b) => b.value);
  const min = Math.min(0, ...values);
  const max = Math.max(0, ...values);
  const span = Math.max(1e-9, max - min);
  const plotHeight = PLOT_BOTTOM - PLOT_TOP;

  function yFor(v: number): number {
    return PLOT_BOTTOM - ((v - min) / span) * plotHeight;
  }

  const n = bars.length;
  const barAreaWidth = (VB_WIDTH - GAP * (n + 1)) / n;
  const barWidth = Math.min(34, Math.max(2, barAreaWidth * 0.72));
  const zeroY = yFor(0);

  function colorFor(v: number): string {
    if (mode === 'accent') return accent;
    return v >= 0 ? COLOR_POSITIVE : COLOR_NEGATIVE;
  }

  return (
    <svg
      class="waterfall-svg"
      viewBox={`${-LABEL_PAD_LEFT} 0 ${VB_WIDTH + LABEL_PAD_LEFT} ${VB_HEIGHT}`}
      role="img"
      aria-label={`Balkendiagramm je Jahr: ${bars.map((b) => `${b.label} ${valueFmt(b.value)}`).join(', ')}`}
    >
      <line x1={0} y1={zeroY} x2={VB_WIDTH} y2={zeroY} stroke="rgba(232,236,244,0.25)" stroke-width="1" />
      {bars.map((bar, i) => {
        const cx = GAP + i * (barAreaWidth + GAP) + barAreaWidth / 2;
        const x = cx - barWidth / 2;
        const isNegative = bar.value < 0;
        const yTop = yFor(Math.max(0, bar.value));
        const yBottom = yFor(Math.min(0, bar.value));
        const height = Math.max(1, yBottom - yTop);
        const labelY = isNegative ? yBottom + 11 : yTop - 5;
        const barFontSize = n > 12 ? 7.5 : 9;
        const yearFontSize = n > 12 ? 8 : 9.5;

        return (
          <g key={bar.key}>
            <rect x={x} y={yTop} width={barWidth} height={height} fill={colorFor(bar.value)} rx={1.5}>
              <title>
                {bar.label}: {valueFmt(bar.value)}
              </title>
            </rect>
            <text x={cx} y={labelY} text-anchor="middle" font-size={barFontSize} fill="#e8ecf4">
              {valueFmt(bar.value)}
            </text>
            <text
              x={cx}
              y={PLOT_BOTTOM + 15}
              text-anchor="end"
              font-size={yearFontSize}
              fill="#93a0b8"
              transform={`rotate(-40 ${cx} ${PLOT_BOTTOM + 15})`}
            >
              {bar.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
